import React, { useState } from 'react';
import { api } from '@/lib/api';
import { Send, Sparkles, Loader2, MessageSquareText } from 'lucide-react';

const SUGGESTIONS = [
    'Which courses have the lowest completion rates?',
    'How are our departments performing?',
    'Who are the top learners this month?',
    'Which game has the highest engagement?',
    'How many certifications have been issued?',
];

export default function AdminAIAnalytics() {
    const [messages, setMessages] = useState([
        { role: 'assistant', content: 'Hi! I\'m the Propulse Analytics Assistant. Ask me anything about your learners, courses, certifications, or games — I\'ll answer using live data only.' },
    ]);
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);

    const ask = async (text) => {
        const q = (text ?? input).trim();
        if (!q) return;
        setMessages((m) => [...m, { role: 'user', content: q }]);
        setInput('');
        setBusy(true);
        try {
            const { data } = await api.post('/admin/ai/analytics', { question: q });
            setMessages((m) => [...m, { role: 'assistant', content: data.answer }]);
        } catch (e) {
            setMessages((m) => [...m, { role: 'assistant', content: `Sorry — ${e?.response?.data?.detail || 'analytics call failed'}.` }]);
        } finally { setBusy(false); }
    };

    return (
        <div className="max-w-3xl mx-auto" data-testid="admin-ai-analytics-page">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-brand-blue to-brand-red text-white text-xs font-bold uppercase tracking-wide">
                <Sparkles className="h-3.5 w-3.5" /> AI Analytics
            </div>
            <h1 className="font-display font-extrabold text-3xl mt-3 text-slate-900">Ask your data anything.</h1>
            <p className="text-sm text-slate-500 mt-1">Natural-language Q&A grounded in your real MongoDB metrics. Zero hallucination.</p>

            <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-4 space-y-3 min-h-[480px]" data-testid="ai-chat-window">
                {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div
                            className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                                m.role === 'user'
                                    ? 'bg-brand-blue text-white rounded-tr-sm'
                                    : 'bg-slate-100 text-slate-800 rounded-tl-sm'
                            }`}
                            data-testid={`ai-msg-${m.role}-${i}`}
                        >
                            {m.content}
                        </div>
                    </div>
                ))}
                {busy && (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" /> Querying live metrics…
                    </div>
                )}
            </div>

            {messages.length <= 1 && (
                <div className="mt-3 flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s, i) => (
                        <button
                            key={i}
                            onClick={() => ask(s)}
                            className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-brand-blue text-slate-700"
                            data-testid={`ai-suggestion-${i}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}

            <div className="mt-3 flex gap-2">
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !busy && ask()}
                    placeholder="Ask about completion rates, top learners, etc."
                    className="flex-1 px-4 py-3 rounded-2xl border border-slate-200 text-sm"
                    data-testid="ai-input"
                />
                <button
                    onClick={() => ask()}
                    disabled={busy}
                    className="px-5 rounded-2xl bg-brand-blue text-white font-semibold disabled:opacity-60 inline-flex items-center gap-2"
                    data-testid="ai-send-btn"
                >
                    <Send className="h-4 w-4" /> Send
                </button>
            </div>
        </div>
    );
}
